/**
 * 基础组件
 *
 * @author wangjx
 */
import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import throttle from 'lodash/throttle';
import MenufoldOutlined from '@haier/icons/MenufoldOutlined';
import MenuUnfoldOutlined from '@haier/icons/MenuUnfoldOutlined';
import { ConfigContext } from '../config-provider/context';
import Layout from '../layout';
import Drawer from '../drawer';
import Space from '../space';
import Logo from './components/Logo';
import Navigation from './components/Navigation';
import User from './components/User';
import MainPageHeader from './components/MainPageHeader';
import MainTabs from './components/MainTabs';
import { getNavigationByKey, flattenTreeDataClosure, findParent, getParentNoSubPageKey, } from './utils';
import useBuriedPoint from '../_util/hooks/useBuriedPoint';
const { Header, Sider, Content, Footer } = Layout;
export const MainFrameContext = React.createContext({});
const InternalMainFrame = (props, ref) => {
    const { theme, layout = 'side', title, navigation, children, selectedMenuKey, user, footer, menuExpansionStrategy, headerExtra, fixedHeader, renderLogo, onNavigate, className, showTabs = false, style, fixedTabs, } = props;
    const { getPrefixCls } = React.useContext(ConfigContext);
    const prefixCls = getPrefixCls('mainframe');
    const [collapsed, setCollapsed] = useState(false);
    const [collapsedWidth, setCollapsedWidth] = useState(80);
    const [siderWidth, setSiderWidth] = useState(256);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [openKeys, setOpenKeys] = useState([]);
    // v3.4.14
    const [internalSelectMenuKey, setInternalSelectMenuKey] = useState(selectedMenuKey);
    const [tabsData, setTabsData] = useState([]);
    //  组件上报
    useBuriedPoint('MainFrame');
    useEffect(() => {
        if (showTabs) {
            // 获取页签数据
            const tabsDataStr = localStorage.getItem('bwd-mainframe-tabsData');
            const initTabsData = tabsDataStr ? JSON.parse(tabsDataStr) : [];
            setTabsData(initTabsData);
        }
    }, []);
    useEffect(() => {
        var _a;
        // 存储页签数据
        if (showTabs) {
            localStorage.setItem('bwd-mainframe-tabsData', (_a = JSON.stringify(tabsData)) !== null && _a !== void 0 ? _a : []);
        }
    }, [tabsData]);
    useEffect(() => {
        if (showTabs) {
            setInternalSelectMenuKey(selectedMenuKey);
            addTabs(selectedMenuKey);
        }
    }, [selectedMenuKey, navigation]);
    const [crumbData, selectedKeys] = React.useMemo(() => {
        var _a;
        const result = flattenTreeDataClosure(navigation !== null && navigation !== void 0 ? navigation : []);
        const crumbData = findParent(selectedMenuKey, result);
        const selectedKey = getParentNoSubPageKey(selectedMenuKey, result);
        if (menuExpansionStrategy === 'expandSecondary' && !openKeys.length) {
            // 默认展开二级菜单
            const oKeys = (_a = result === null || result === void 0 ? void 0 : result.filter((item) => item.children).map((item) => item === null || item === void 0 ? void 0 : item.key)) !== null && _a !== void 0 ? _a : [];
            setOpenKeys(oKeys);
        }
        else if (menuExpansionStrategy === 'accordion') {
            // 点击之后，展开当前菜单，其他菜单收起
            setOpenKeys(crumbData.map((item) => {
                var _a;
                return (_a = item === null || item === void 0 ? void 0 : item.key) !== null && _a !== void 0 ? _a : '';
            }));
        }
        else {
            // 手动展开的菜单，点击菜单还会展开当前所有展开项
            const resetOpenKeys = crumbData
                .filter((item) => openKeys.indexOf(item === null || item === void 0 ? void 0 : item.key) === -1)
                .map((item) => item === null || item === void 0 ? void 0 : item.key);
            setOpenKeys([...(openKeys !== null && openKeys !== void 0 ? openKeys : []), ...(resetOpenKeys !== null && resetOpenKeys !== void 0 ? resetOpenKeys : [])]);
        }
        return [crumbData, [selectedKey]];
    }, [selectedMenuKey, navigation]);
    // 添加页签
    const addTabs = (activeKey) => {
        var _a;
        const hasTab = tabsData === null || tabsData === void 0 ? void 0 : tabsData.findIndex((v) => {
            return v.key === activeKey;
        });
        if (hasTab !== -1)
            return;
        const result = (_a = flattenTreeDataClosure(navigation !== null && navigation !== void 0 ? navigation : [])) === null || _a === void 0 ? void 0 : _a.filter((item) => {
            return item.key === activeKey;
        });
        if (result && result.length)
            setTabsData([...tabsData, result[0]]);
    };
    const calculateLayout = () => {
        const bodyClientWidth = window.document.body.clientWidth;
        if (bodyClientWidth <= 768) {
            setSiderWidth(0);
            setCollapsedWidth(0);
            setCollapsed(true);
        }
        else if (990 >= bodyClientWidth && bodyClientWidth > 768) {
            setSiderWidth(208);
            setCollapsedWidth(80);
            setDrawerVisible(false);
            setCollapsed(true);
        }
        else if (1440 >= bodyClientWidth && bodyClientWidth > 990) {
            setSiderWidth(208);
            setCollapsedWidth(80);
            setDrawerVisible(false);
            setCollapsed(false);
        }
        else {
            setSiderWidth(256);
            setCollapsedWidth(80);
            setDrawerVisible(false);
            setCollapsed(false);
        }
    };
    // 侧栏收缩事件
    const toggleCollapsed = () => {
        setDrawerVisible(!drawerVisible);
        setCollapsed(!collapsed);
    };
    React.useLayoutEffect(() => {
        // 加载页面时执行一次
        calculateLayout();
        // 监听浏览器宽度的改变
        window.addEventListener('resize', throttle(calculateLayout, 300));
        return () => {
            window.removeEventListener('resize', throttle(calculateLayout, 300));
        };
    }, []);
    React.useImperativeHandle(ref, () => {
        return {};
    });
    // 导航点击事件
    const handleNavigationClick = (info) => {
        const item = getNavigationByKey(info.key, navigation !== null && navigation !== void 0 ? navigation : []);
        // 如果显示抽屉，点击菜单后关闭抽屉
        if (siderWidth === 0 && drawerVisible)
            toggleCollapsed();
        if (onNavigate)
            onNavigate(item);
    };
    const handleOpenChange = (openKeys) => setOpenKeys(openKeys);
    // 点击页签选中
    const handleTabsChange = (activeKey) => {
        const tabs = tabsData === null || tabsData === void 0 ? void 0 : tabsData.filter((item) => {
            return item.key === activeKey;
        });
        if (!tabs || !tabs.length)
            return;
        if (onNavigate)
            onNavigate(tabs[0]);
    };
    // 标签页新增或删除回调事件
    const handleEdit = (targetKey, action) => {
        if (action === 'remove')
            removeTabs(targetKey);
    };
    // 删除页签
    const removeTabs = (targetKey) => {
        const index = tabsData === null || tabsData === void 0 ? void 0 : tabsData.findIndex((item) => {
            return item.key === targetKey;
        });
        const tabs = tabsData.filter((item) => {
            return item.key !== targetKey;
        });
        setTabsData(tabs);
        const tab = tabs[index - 1] || tabs[index];
        if (!tab) {
            window.location.href = '/';
            return;
        }
        if (targetKey === internalSelectMenuKey) {
            if (onNavigate)
                onNavigate(tab);
        }
    };
    const LogoNode = React.createElement(Logo, { collapsed: collapsed, render: renderLogo });
    const NavigationNode = (React.createElement(Navigation, { theme: theme, data: navigation, onClick: handleNavigationClick, selectedKeys: selectedKeys, openKeys: openKeys, onOpenChange: handleOpenChange }));
    const renderHeader = () => {
        const userNode = React.createElement(User, Object.assign({}, user));
        const titleNode = title && React.createElement("div", { className: `${prefixCls}-title` }, title);
        const headerCls = classnames(`${prefixCls}-header`, {
            [`${prefixCls}-header-fixed`]: fixedHeader,
        });
        if (layout === 'top') {
            return (React.createElement(Header, { className: headerCls },
                React.createElement("div", { className: `${prefixCls}-nav-header` },
                    React.createElement("div", { className: `${prefixCls}-nav-header-left` },
                        React.cloneElement(LogoNode, { collapsed: false }),
                        titleNode),
                    React.createElement("div", { className: `${prefixCls}-nav-header-menu` }, React.cloneElement(NavigationNode, { mode: 'horizontal', openKeys: undefined })),
                    React.createElement("div", { className: `${prefixCls}-nav-header-right` },
                        React.createElement(Space, { size: "large" },
                            headerExtra,
                            userNode)))));
        }
        return (React.createElement(Header, { className: headerCls },
            React.createElement("span", { className: `${prefixCls}-trigger`, onClick: toggleCollapsed }, collapsed ? React.createElement(MenuUnfoldOutlined, null) : React.createElement(MenufoldOutlined, null)),
            titleNode,
            React.createElement("div", { className: `${prefixCls}-header-right` },
                React.createElement(Space, { size: "large" },
                    headerExtra,
                    userNode))));
    };
    const renderSider = () => {
        if (layout === 'top')
            return;
        if (siderWidth === 0)
            return;
        return (React.createElement(Sider, { className: `${prefixCls}-sider`, width: siderWidth, collapsed: collapsed, theme: theme },
            LogoNode,
            NavigationNode));
    };
    const renderDrawerSider = () => {
        return (React.createElement(Sider, { className: `${prefixCls}-sider`, width: 256, theme: theme },
            React.createElement(Logo, { render: renderLogo }),
            React.createElement(Navigation, { theme: theme, data: navigation, onClick: handleNavigationClick, selectedKeys: selectedKeys, openKeys: openKeys, onOpenChange: handleOpenChange })));
    };
    const renderTabs = () => {
        return (React.createElement(MainTabs, { fixedTabs: fixedTabs, prefixCls: prefixCls, mainTabs: {
                items: tabsData,
                activeKey: internalSelectMenuKey,
                onChange: handleTabsChange,
                onEdit: handleEdit,
            } }));
    };
    const mainframeCls = classnames(prefixCls, className, {
        [`${prefixCls}-${theme}`]: theme,
        [`${prefixCls}-layout-${layout}`]: true,
    });
    return (React.createElement(MainFrameContext.Provider, { value: {
            prefixCls,
            collapsed,
            crumbData: crumbData,
        } },
        React.createElement(Layout, { className: mainframeCls, style: style },
            renderSider(),
            React.createElement(Layout, { className: classnames({
                    [`${prefixCls}-layout-header-fixed`]: fixedHeader && showTabs,
                }), style: {
                    marginLeft: layout === 'top'
                        ? undefined
                        : collapsed
                            ? collapsedWidth
                            : siderWidth === 0
                                ? 0
                                : siderWidth,
                } },
                renderHeader(),
                showTabs ? renderTabs() : null,
                React.createElement(Content, { className: `${prefixCls}-content` }, children),
                footer && React.createElement(Footer, { className: `${prefixCls}-footer` }, footer)),
            React.createElement(Drawer, { className: classnames(`${prefixCls}-drawer`, {
                    [`${prefixCls}-drawer-${theme}`]: theme,
                }), open: siderWidth === 0 && drawerVisible, placement: "left", width: 256, closable: false, onClose: () => {
                    toggleCollapsed();
                } }, renderDrawerSider()))));
};
const ForwardMainFrame = React.forwardRef(InternalMainFrame);
const MainFrame = ForwardMainFrame;
MainFrame.defaultProps = {
    theme: 'dark',
    menuExpansionStrategy: 'default',
    fixedHeader: true,
    fixedTabs: true,
    showTabs: false,
};
MainFrame.MainPageHeader = MainPageHeader;
export default MainFrame;
